/**
 * Phase B — Liste des chantiers.
 *
 * Fetch parallèle `/chantiers` + `/leads` pour enrichir le nom client (le
 * backend ne renvoie que `lead_id`). Filtre par statut, empty/loading/error
 * soignés, thème light + dark via variables CSS `platform-*`.
 *
 * Champs affichés : référence CHT-YYYY-NNN, nom du chantier, client,
 * statut (badge coloré), date de démarrage prévue, action "ouvrir".
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Hammer, ArrowRight, AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

// Ordre pipeline chantier — reflète le flow métier (attente RDV → devise →
// à planifier → en cours → finalisé → terminé).
const STATUS_META = {
  en_attente_rdv: { label: "En attente RDV", cls: "badge-neutral" },
  devise: { label: "Devisé", cls: "badge-warning" },
  a_planifier: { label: "À planifier", cls: "badge-warning" },
  en_cours: { label: "En cours", cls: "badge-info" },
  finalise: { label: "Finalisé", cls: "badge-success" },
  termine: { label: "Terminé", cls: "badge-neutral" },
};

const STATUS_ORDER = ["en_attente_rdv", "devise", "a_planifier", "en_cours", "finalise", "termine"];

function formatDate(v) {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleDateString("fr-BE", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "—";
  }
}

export default function ChantiersListPage() {
  const [items, setItems] = useState([]);
  const [leadsById, setLeadsById] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("__all__");

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch en parallèle — le backend ne joint pas le client, on enrichit
      // côté frontend (skeleton Phase B, endpoint dédié optionnel plus tard).
      const [chantiersRes, leadsRes] = await Promise.all([
        api.get("/chantiers"),
        api.get("/leads", { params: { page_size: 500 } }),
      ]);
      setItems(Array.isArray(chantiersRes.data) ? chantiersRes.data : []);
      const map = {};
      for (const l of leadsRes.data.items || []) {
        map[l.id] = l;
      }
      setLeadsById(map);
    } catch (err) {
      const msg = formatApiError(err, "Impossible de charger les chantiers");
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (statusFilter === "__all__") return items;
    return items.filter((c) => c.status === statusFilter);
  }, [items, statusFilter]);

  // Compteurs par statut — pour l'aperçu dans le select
  const countsByStatus = useMemo(() => {
    const c = {};
    for (const s of STATUS_ORDER) c[s] = 0;
    for (const it of items) c[it.status] = (c[it.status] || 0) + 1;
    return c;
  }, [items]);

  const clientNameFor = (leadId) => {
    const l = leadsById[leadId];
    if (!l) return "—";
    const full = `${l.first_name || ""} ${l.last_name || ""}`.trim();
    return full || l.email || "—";
  };

  return (
    <div className="space-y-6" data-testid="chantiers-list-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="inline-flex items-center gap-2 text-2xl font-bold text-platform-heading">
            <Hammer className="h-5 w-5 text-[#C4553B]" /> Chantiers
          </h1>
          <p className="text-sm text-platform-body">
            Liste des chantiers actifs. Chaque chantier découle d&apos;un devis signé sur un projet.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          className="border-platform-border bg-platform-surface text-platform-body"
          data-testid="chantiers-refresh"
        >
          <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Rafraîchir
        </Button>
      </div>

      {/* Filtres */}
      <Card className="border-platform-border bg-platform-surface">
        <CardContent className="flex flex-wrap items-center gap-3 p-4">
          <label className="text-xs uppercase tracking-wide text-platform-muted">Statut</label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger
              className="w-[220px] border-platform-border bg-platform-surfaceAlt text-platform-heading"
              data-testid="chantiers-status-filter"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tous ({items.length})</SelectItem>
              {STATUS_ORDER.map((s) => (
                <SelectItem key={s} value={s}>
                  {STATUS_META[s].label} ({countsByStatus[s] || 0})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="ml-auto text-xs text-platform-muted" data-testid="chantiers-count">
            {loading ? "…" : `${filtered.length} affiché${filtered.length > 1 ? "s" : ""}`}
          </span>
        </CardContent>
      </Card>

      {/* Corps : loading / error / empty / table */}
      <Card className="border-platform-border bg-platform-surface">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-14 text-sm text-platform-muted">
              <Loader2 className="h-4 w-4 animate-spin" /> Chargement des chantiers…
            </div>
          ) : error ? (
            <div className="flex flex-col items-center gap-3 py-14 text-center" data-testid="chantiers-error">
              <AlertTriangle className="h-8 w-8 text-[#D99A2B]" />
              <div className="text-sm text-platform-body">{error}</div>
              <Button variant="outline" size="sm" onClick={load} className="border-platform-border">
                Réessayer
              </Button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-16 text-center" data-testid="chantiers-empty">
              <div className="rounded-full bg-[#C4553B]/10 p-3">
                <Hammer className="h-8 w-8 text-[#C4553B]" />
              </div>
              <div className="max-w-md text-sm text-platform-body">
                {statusFilter === "__all__" ? (
                  <>
                    Aucun chantier pour l&apos;instant. Un chantier est créé automatiquement dès qu&apos;un
                    devis est marqué signé sur un projet.
                  </>
                ) : (
                  <>Aucun chantier avec le statut “{STATUS_META[statusFilter].label}”.</>
                )}
              </div>
              {statusFilter !== "__all__" && (
                <Button variant="outline" size="sm" onClick={() => setStatusFilter("__all__")} className="border-platform-border">
                  Voir tous les chantiers
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-platform-border bg-platform-surfaceAlt text-left text-xs uppercase tracking-wider text-platform-muted">
                  <tr>
                    <th className="px-4 py-2.5">Référence</th>
                    <th className="px-4 py-2.5">Nom</th>
                    <th className="px-4 py-2.5">Client</th>
                    <th className="px-4 py-2.5">Statut</th>
                    <th className="px-4 py-2.5">Démarrage prévu</th>
                    <th className="px-4 py-2.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c) => {
                    const meta = STATUS_META[c.status] || STATUS_META.en_attente_rdv;
                    return (
                      <tr
                        key={c.id}
                        className="border-b border-platform-border/50 transition-colors hover:bg-platform-hover"
                        data-testid={`chantiers-row-${c.id}`}
                      >
                        <td className="px-4 py-2.5 font-mono text-xs text-platform-body">
                          {c.reference_code || "—"}
                        </td>
                        <td className="px-4 py-2.5 text-platform-heading">
                          {c.name || "(sans nom)"}
                        </td>
                        <td className="px-4 py-2.5 text-platform-body" data-testid={`chantiers-client-${c.id}`}>
                          {clientNameFor(c.lead_id)}
                        </td>
                        <td className="px-4 py-2.5">
                          <Badge className={`border ${meta.cls}`}>{meta.label}</Badge>
                        </td>
                        <td className="px-4 py-2.5 text-platform-body">
                          {formatDate(c.expected_start_date)}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <Link
                            to={`/chantiers/${c.id}`}
                            className="inline-flex items-center gap-1 text-[#C4553B] hover:text-[#E07A62]"
                            data-testid={`chantiers-open-${c.id}`}
                          >
                            Ouvrir <ArrowRight className="h-3.5 w-3.5" />
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
